import React, { useState, useEffect } from "react";
import { Auth } from "../../../Common/lib/auth";
import { HiXCircle, HiOutlineEye } from "react-icons/hi";

export default function PasswordStep({ userInfo, setUserInfo, currentStep, setCurrentStep, classNames }) {
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({
    mainError: "",
    passwordError: "",
    secondPasswordError: "",
    termsAndPolicyError: "",
  });

  const requiredFields = ["password", "secondPassword"];

  // Handles clearing errors when a user starts editing a field.
  useEffect(() => {
    for (let i = 0; i < requiredFields.length; i++) {
      const field = requiredFields[i];
      if (userInfo[field].length > 0) {
        setErrors(prevState => ({
          ...prevState,
          [`${field}Error`]: "",
        }));
      }
    }
  }, [userInfo]);

  /**
   * Moves user to the next step of the sign up process. Validates before moving to the next step.
   */
  async function nextStep() {
    if (validateStep()) {
      // Creates the params object that will contain all user data necessary to create new account
      const params = {
        username: userInfo.email,
        password: userInfo.password,
        attributes: {
          email: userInfo.email,
          given_name: userInfo.firstName,
          family_name: userInfo.lastName,
          phone_number: "+1"+userInfo.phoneNumber,
          institute: userInfo.institute,
          research_field: userInfo.fieldOfResearch,
          research_geo: userInfo.geoLocationOfResearch,
          edit_locked: "false",
          dv_approval_required: "false"
          ,
          // Indicate acceptance of legal docs so backend can record versions
          terms_acknowledged: userInfo.termsAndPolicy,
          privacy_acknowledged: userInfo.termsAndPolicy,
        },
        autoSignIn: {
          enabled: true
        }
      };

      try {
        // Creates new user account
        await Auth.signUp(params);
        setCurrentStep(currentStep + 1);
      }
      catch (error) {
        // console.log(error.message);
        setErrors(prevState => ({
          ...prevState,
          mainError: error.message,
        }));
      }
    }
  }

  /**
   * Moves user to the previous step of the sign up process.
   */
  function backStep() {
    setCurrentStep(currentStep - 1)
  }

  /**
   * Validates the current step of the sign up page.
   * @returns {boolean} Validation result. True if form is valid, false if form is invalid.
   */
  function validateStep() {
    let isValid = true;

    // Check if any of the fields are empty
    if (userInfo.password === "" || userInfo.secondPassword === "") {
      if (userInfo.password === "") {
        setErrors(prevState => ({
          ...prevState,
          passwordError: "Required",
        }));
      }
      if (userInfo.secondPassword === "") {
        setErrors(prevState => ({
          ...prevState,
          secondPasswordError: "Required",
        }));
      }
      return false;
    }

    // Check that the password is at least 8 characters in length
    if (userInfo.password.length < 8) {
      setErrors(prevState => ({
        ...prevState,
        passwordError: "Must be at least 8 characters long",
      }));
      return false;
    }

    // Check that the password doesn't start or end with a space character (Cognito doesn't allow it)
    if (!(/^[\S]+.*[\S]+$/).test(userInfo.password)) {
      setErrors(prevState => ({
        ...prevState,
        passwordError: "Cannot start or end with a space",
      }));
      isValid = false;
    }

    // Check that the two passwords match each other
    if (userInfo.secondPassword !== userInfo.password) {
      setErrors(prevState => ({
        ...prevState,
        secondPasswordError: "Passwords must match",
      }));
      isValid = false;
    }

    // Check that the user accepted terms and conditions
    if (!userInfo.termsAndPolicy) {
      setErrors(prevState => ({
        ...prevState,
        termsAndPolicyError: "err",
      }));
      isValid = false;
    }

    return isValid;
  }

  /**
   * Handles input field changes.
   * @param {*} field Name of userInfo property to change
   * @param {*} value Value from input field
   */
  function handleChange(field, value) {
    setUserInfo((prevState) => ({
      ...prevState,
      [field]: value,
    }));
  }

  return (
    <>
      <div className="mb-6 sm:col-span-2">
        <label htmlFor="password" className="items-center flex text-sm sm:text-base font-medium text-gray-700 dark:text-white">
          Password
          {errors.passwordError.length > 0 &&
            <p className="italic text-xs sm:text-sm block font-base font-normal text-red-600">
              &nbsp;- {errors.passwordError}
            </p>
          }
        </label>
        <div className="mt-1">
          <input
            id="password"
            onChange={(e) => handleChange("password", e.target.value)}
            data-cy="password"
            value={userInfo.password}
            type={showPassword ? "text" : "password"}
            className={classNames(errors.passwordError.length > 0 ? 'border-red-600 dark:border-red-500' : 'border-gray-300 dark:border-gray-600', 'border outline-none focus:ring-1 text-sm sm:text-base py-3 px-4 block w-full shadow-sm focus:ring-blue-500 focus:border-blue-500 rounded-md dark:bg-gray-700 dark:text-white')}
          />
        </div>
      </div>

      <div className="mb-6 sm:col-span-2">
        <label htmlFor="confirmPassword" className="items-center flex text-sm sm:text-base font-medium text-gray-700 dark:text-white">
          Confirm Password
          {errors.secondPasswordError.length > 0 &&
            <p className="italic text-xs sm:text-sm block font-base font-normal text-red-600">
              &nbsp;- {errors.secondPasswordError}
            </p>
          }
        </label>
        <div className="mt-1 flex justify-end items-center">
          <input
            id="confirmPassword"
            onChange={(e) => handleChange("secondPassword", e.target.value)}
            data-cy="confirm-password"
            value={userInfo.secondPassword}
            type={showPassword ? "text" : "password"}
            className={classNames(errors.secondPasswordError.length > 0 ? 'border-red-600 dark:border-red-500' : 'border-gray-300 dark:border-gray-600', 'border outline-none focus:ring-1 text-sm sm:text-base py-3 px-4 block w-full shadow-sm focus:ring-blue-500 focus:border-blue-500 rounded-md dark:bg-gray-700 dark:text-white')}
            />
            {/* <HiOutlineEye className="absolute justify-end h-6 w-6 cursor-pointer mr-3 text-gray-500" onClick={() => setShowPassword(prevPassword => !prevPassword)}/> */}
        </div>
      </div>

      {/* <div className="text-sm sm:text-base mb-6">
        <label className="font-medium text-gray-700">
          <input
            onChange={() => {setShowPassword(prevPassword => !prevPassword)}}
            type="checkbox"
            className={classNames('border-gray-400', 'mr-2 h-5 w-5 rounded text-blue-700 focus:ring-blue-700')}
          />
          Show Password
        </label>
      </div> */}

      <div className="text-sm sm:text-base mb-6">
        <label className="flex flex-wrap items-center font-medium text-gray-700 dark:text-gray-400">
          <input
            onChange={(e) => handleChange("termsAndPolicy", !userInfo.termsAndPolicy)}
            data-cy="privacy-policy"
            checked={userInfo.termsAndPolicy}
            type="checkbox"
            className={classNames(errors.termsAndPolicyError.length > 0 ? 'border-red-600 dark:border-red-500' : 'border-gray-400 dark:border-gray-600', 'mr-2 h-5 w-5 rounded text-blue-700 focus:ring-blue-700 dark:bg-gray-700')}
          />
          I agree with the &nbsp;<a onClick={() => window.open("/privacy-policy")} className="font-medium text-blue-700 underline dark:text-blue-500">Privacy Policy</a>&nbsp; and &nbsp;<a onClick={() => window.open("/terms-and-conditions")} className="font-medium text-blue-700 underline  dark:text-blue-500">Terms of Use</a>
        </label>
      </div>

      {errors.mainError.length > 0 &&
        <div className="mb-6 rounded-md bg-red-100 p-4">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <HiXCircle className="h-5 w-5 text-red-400" aria-hidden="true" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">There was an error creating your account.</h3>
            </div>
          </div>
        </div>
      }

      <div className="text-sm sm:text-base gap-14 sm:gap-24 flex justify-between">
        <button
          onClick={() => backStep()}
          type="button"
          className="rounded-md w-full text-gray-700 bg-gray-300 hover:bg-gray-400 py-2.5 px-3.5 text-md font-semibold shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 border dark:bg-gray-800 dark:text-gray-200 dark:border-gray-600 dark:hover:text-white dark:hover:bg-gray-700"
        >
          Back
        </button>
        <button
          onClick={() => nextStep()}
          data-cy="next"
          type="button"
          className="rounded-md w-full bg-blue-600 py-2.5 px-3.5 text-md font-semibold text-white shadow-sm hover:bg-blue-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          Create Account
        </button>
      </div>
    </>
  )
}